import { useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CameraButtons } from "./CameraButtons";
import { FileText, Trash2, ArrowUp, ArrowDown, Loader2, ScanText } from "lucide-react";

const TYPES = { ankaufvertrag: "Ankaufvertrag", rechnung: "Rechnung", ausweisdokument: "Ausweisdokument", produktdokument: "Produktdokument", verkaufsdokument: "Verkaufsdokument", sonstiges: "Sonstiges" };

export function DocScan({ open, onClose, onSaved, defaultType = "sonstiges" }) {
  const [pages, setPages] = useState([]);
  const [title, setTitle] = useState("");
  const [type, setType] = useState(defaultType);
  const [saving, setSaving] = useState(false);
  const [ocr, setOcr] = useState(null);
  const [ocrBusy, setOcrBusy] = useState(false);

  const add = (imgs) => setPages((p) => [...p, ...imgs.map((d) => ({ data: d }))]);
  const move = (i, dir) => {
    const next = [...pages];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setPages(next);
  };
  const remove = (i) => setPages(pages.filter((_, x) => x !== i));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/documents/scan-pdf", {
        title: title || `Scan ${new Date().toLocaleDateString("de-DE")}`,
        type, pages: pages.map((p) => p.data),
      });
      toast.success(`Dokument „${data.title}“ mit ${pages.length} Seite(n) als PDF gespeichert.`);
      setPages([]); setTitle(""); setOcr(null);
      onSaved?.(data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const runOcr = async () => {
    setOcrBusy(true);
    try {
      const { data } = await api.post("/ki/analyze", { input_type: "foto", value: "Dokumentenscan: extrahiere alle erkennbaren Textdaten, Beträge, Namen und Nummern.", file_base64: pages[0].data });
      setOcr(data.extracted);
      toast.success("Text wurde erkannt – bitte prüfen.");
    } catch (e) { toast.error(apiError(e, "Texterkennung fehlgeschlagen")); }
    setOcrBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setPages([]); setOcr(null); } onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="doc-scan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanText className="w-5 h-5" /> Dokument scannen</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <CameraButtons onCapture={add} cameraLabel="Seite fotografieren" galleryLabel="Aus Galerie" />
          {pages.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="doc-scan-pages">
              {pages.map((p, i) => (
                <div key={i} className="relative border border-zinc-200 rounded-lg overflow-hidden group">
                  <img src={p.data} alt={`Seite ${i + 1}`} className="w-full h-28 object-cover" />
                  <div className="absolute top-1 left-1 bg-zinc-900/80 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">{i + 1}</div>
                  <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-white/90 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => move(i, -1)} className="p-1.5" data-testid={`page-up-${i}`}><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => move(i, 1)} className="p-1.5" data-testid={`page-down-${i}`}><ArrowDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(i)} className="p-1.5 text-red-600" data-testid={`page-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {pages.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Titel</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Kaufvertrag AN-…" data-testid="doc-scan-title" className="mt-1.5" /></div>
                <div><Label>Dokumenttyp</Label>
                  <select value={type} onChange={(e) => setType(e.target.value)} data-testid="doc-scan-type" className="mt-1.5 w-full border border-zinc-200 rounded-lg px-3 py-2 text-sm bg-white min-h-[44px]">
                    {Object.entries(TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={runOcr} disabled={ocrBusy} className="flex-1 min-h-[44px]" data-testid="doc-scan-ocr-button">
                  {ocrBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanText className="w-4 h-4 mr-2" />} Text erkennen (KI)
                </Button>
                <Button onClick={save} disabled={saving} className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white min-h-[44px]" data-testid="doc-scan-save-button">
                  {saving ? "Speichern…" : <><FileText className="w-4 h-4 mr-2" /> Als PDF speichern</>}
                </Button>
              </div>
              {ocr && (
                <div className="border border-zinc-200 rounded-lg p-3 bg-zinc-50 text-xs font-mono whitespace-pre-wrap max-h-48 overflow-y-auto" data-testid="doc-scan-ocr-result">
                  {JSON.stringify(ocr, null, 2)}
                </div>
              )}
            </>
          )}
          {pages.length === 0 && <p className="text-xs text-zinc-400 text-center">Kamera öffnen, Dokument aufnehmen – mehrere Seiten möglich, danach sortieren und als PDF speichern.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
